/* eslint-disable @typescript-eslint/no-explicit-any */
import { combineLatest, firstValueFrom, map, Observable, ReplaySubject, shareReplay } from 'rxjs';
import { CacheBasicConfig, CacheConfig, CacheMultiKeyConfig, types } from './interfaces';
import { CacheStorage } from './cache.storage';
import { CacheUtils } from './cache.utils';

/**
 * CacheService stores the results of method calls and returns the cached value when the same method is called with the same arguments.
 * The configuration dictates how the cache is managed for each method.
 * @note this is a short lived cache, it does not persist between page reloads.
 */
export class CacheService<T> {
  applyConfig(instance: T, config: CacheConfig<any>[]): void {
    config.forEach((c) => this.setupMethod(instance, c));
  }

  private newStorage(config: CacheConfig<T>): CacheStorage<T> {
    return new CacheStorage(config);
  }

  private setupMethod(instance: T, config: CacheConfig<T>): void {
    const sourceFn = (instance as any)[config.method];
    if (CacheUtils.isFunction(sourceFn)) {
      const cache = this.newStorage(config);

      const wrapperFn = (...args: any[]): unknown => {
        if (CacheUtils.isCacheBasicConfig(config)) {
          return this.setupMethodForSimpleConfig(cache, instance, sourceFn, config, args);
        }
        if (CacheUtils.isCacheMultiKeyConfig(config)) {
          return this.setupMethodForMultiKeyConfig(cache, instance, sourceFn, config, args);
        }
        return sourceFn.bind(instance)(...args);
      };
      (instance as any)[config.method] = wrapperFn;
    } else {
      throw new Error(`Method ${String(config.method)} not found in the provided instance`);
    }
  }

  private setupMethodForSimpleConfig(
    cache: CacheStorage<T>,
    instance: T,
    sourceFn: types.Func,
    config: CacheBasicConfig<T>,
    args: any[],
  ) {
    const key = CacheUtils.generateSimpleKey(args, config.keys);
    // find existing cache record
    const cacheValue = cache.get(key);
    if (cacheValue !== undefined) {
      return cacheValue;
    }
    // call the source function and store the result in the cache
    let result = sourceFn.bind(instance)(...args);
    if (result instanceof Observable) {
      result = this.applyObservableStrategy(cache, result, config);
    }
    cache.set(key, result);
    return result;
  }

  private setupMethodForMultiKeyConfig(
    cache: CacheStorage<T>,
    instance: T,
    sourceFn: types.Func,
    config: CacheMultiKeyConfig<T>,
    args: any[],
  ) {
    const paramKeys = CacheUtils.generateMultiKeys(args, config.multiKey);
    const missingParamKeys: unknown[] = [];
    const keyMapperFn = config.multiKey.paramKeyMapper ?? CacheUtils.defaultParamKeyMapper;
    // add the missing keys and init them with a ReplaySubject
    paramKeys.forEach((param) => {
      const key = keyMapperFn(param);
      if (!cache.has(key)) {
        missingParamKeys.push(param);
        const value = new ReplaySubject<any>(1);
        cache.set(key, value);
      }
    });
    // call the sourceFn for the missing keys and update the ReplaySubject value
    if (missingParamKeys.length > 0) {
      const newArgs = CacheUtils.replaceMultiKeys(args, config.multiKey, missingParamKeys);
      const result = sourceFn.bind(instance)(...newArgs);
      if (result instanceof Observable) {
        // subscribe to the result and update the ReplaySubject values
        firstValueFrom(result).then((v) => {
          const property = config.multiKey.resultProperty ? v[config.multiKey.resultProperty] : v;
          if (Array.isArray(property) && property.length === missingParamKeys.length) {
            property.forEach((value, index) => {
              const key = keyMapperFn(missingParamKeys[index]);
              const cacheValue = cache.get(key, true) as ReplaySubject<any>;
              cacheValue.next(value);
            });
          } else {
            throw new Error(`${String(config.method)} response size don't match with requested keys`);
          }
        });
      } else {
        // TODO: check if we need to handle non-observable results
        throw new Error('Multi-key configuration only available to Observable results');
      }
    }

    // get all values from the cache in the same order as the requested keys and return the results array
    const observableList = paramKeys
      .map((k) => keyMapperFn(k))
      .map((k) => (cache.get(k, true) as ReplaySubject<unknown>).asObservable());
    const cacheObservables = combineLatest(observableList).pipe(
      map((cacheValues) => {
        if (config.multiKey.resultProperty) {
          const result = { [config.multiKey.resultProperty]: cacheValues };
          return result;
        }
        return cacheValues;
      }),
    );
    return cacheObservables;
  }

  private applyObservableStrategy(
    cache: CacheStorage<T>,
    value: Observable<any>,
    config: CacheBasicConfig<T>,
  ): Observable<any> {
    if (config.strategy?.observable?.share) {
      value = value.pipe(shareReplay(1, cache.maxAge));
    }
    return value;
  }
}
